/** Pure argument/claim mapping for the provider impls — extracted so the
 *  provider-specific translation is unit-testable without rendering hooks
 *  (matches this repo's pure-function test convention). */
import type { AuthUser, GetTokenOptions, LoginOptions } from "./types";

/** Mirror of the old _app.tsx onRedirectCallback: returnTo may carry the Next
 *  basePath ("/web") — Router.replace re-adds it, so strip it to avoid a
 *  doubled "/web/web/..." that 404s to home. */
export const normalizeReturnTo = (returnTo: string | undefined): string => {
  let to = returnTo || "/";
  if (to === "/web") to = "/";
  else if (to.startsWith("/web/")) to = to.slice("/web".length);
  // Open-redirect guard (OWASP unvalidated redirects): only an in-app absolute
  // path may survive — "//host", "https://host", or anything not starting
  // with a single "/" collapses to home.
  if (!/^\/(?!\/)/.test(to)) return "/";
  return to;
};

/** Scope requested at login and token renewal. offline_access is what makes
 *  Auth0 issue a (rotating) refresh token, so silent renewal happens over XHR
 *  instead of the hidden-iframe flow that Safari/Chrome third-party-cookie
 *  blocking breaks. App permissions (read:/write:/delete:) are NOT requested
 *  here — with RBAC (token_dialect access_token_authz) they always arrive in
 *  the token's `permissions` claim regardless of requested scope. */
export const DEFAULT_AUTH_SCOPE = "openid profile email offline_access";

/** An env-provided scope may override the default, but offline_access is
 *  always re-added: env drift must never silently disable refresh tokens
 *  (the failure mode is invisible until sessions start bouncing to login). */
export const resolveAuthScope = (envScope?: string): string => {
  const s = (envScope || "").trim();
  if (!s) return DEFAULT_AUTH_SCOPE;
  return s.split(/\s+/).includes("offline_access") ? s : `${s} offline_access`;
};

export const auth0LoginArgs = (
  opts: LoginOptions | undefined,
  audience: string | undefined,
  scope: string | undefined,
) => ({
  ...(opts?.returnTo !== undefined ? { appState: { returnTo: opts.returnTo } } : {}),
  authorizationParams: { audience, scope },
});

export const auth0TokenArgs = (
  opts: GetTokenOptions | undefined,
  audience: string | undefined,
  scope: string | undefined,
) => ({
  ...(opts?.forceRefresh ? { cacheMode: "off" as const } : {}),
  authorizationParams: { audience, scope },
});

export const oidcSigninArgs = (opts: LoginOptions | undefined) =>
  opts?.returnTo !== undefined ? { state: { returnTo: opts.returnTo } } : {};

export const mapUser = (
  u: { sub?: string; name?: string; email?: string } | undefined | null,
): AuthUser | undefined =>
  u ? { sub: u.sub, name: u.name, email: u.email } : undefined;
